/**
 * Animation clips: procedural poses sampled once at load into the animation atlas (see atlas.ts).
 *
 * A clip sets, for a time `t` in seconds, how far each bone is from its rest pose: an Euler rotation
 * (radians, applied after the rest rotation, XYZ), a translation in its parent's space and a uniform
 * scale. Bones start at rest for every sample, so a clip only touches what moves.
 */
export class BonePose {
  rx = 0;
  ry = 0;
  rz = 0;
  px = 0;
  py = 0;
  pz = 0;
  s = 1;

  /** Adds a rotation (radians). */
  rot(x: number, y = 0, z = 0): this {
    this.rx += x;
    this.ry += y;
    this.rz += z;
    return this;
  }

  /** Adds a translation (model units, parent space). */
  pos(x: number, y = 0, z = 0): this {
    this.px += x;
    this.py += y;
    this.pz += z;
    return this;
  }

  /** Multiplies the scale. */
  scale(k: number): this {
    this.s *= k;
    return this;
  }

  reset(): void {
    this.rx = this.ry = this.rz = this.px = this.py = this.pz = 0;
    this.s = 1;
  }
}

/** A pose being built: one `BonePose` per bone name. Names a rig lacks get a scratch pose (shared clips stay safe). */
export type Bones<N extends string = string> = Record<N, BonePose>;

export interface Clip<N extends string = string> {
  name: string;
  /** Seconds. */
  dur: number;
  /** Loops seamlessly (its pose at `dur` must equal its pose at 0). */
  loop: boolean;
  pose(b: Bones<N>, t: number): void;
  /** Whole-figure squash and stretch (non-uniform, applied on the instance): [sx, sy, sz] at `t`. */
  squash?(t: number): [number, number, number];
  /** Spanish name for the animation sheet (dev only). */
  label?: string;
}

export type ClipSet<N extends string = string> = Clip<N>[];

export const clip = <N extends string>(name: string, dur: number, loop: boolean, pose: (b: Bones<N>, t: number) => void, squash?: (t: number) => [number, number, number], label?: string): Clip<N> => {
  const c: Clip<N> = { name, dur, loop, pose };
  if (squash) c.squash = squash;
  if (label) c.label = label;
  return c;
};
